import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ExternalLink,
  Book,
  FileText,
  Users,
  Globe,
  Mail,
  Share2,
} from "lucide-react";
import Footer from "@/components/Footer";
import { trackPageView } from "@/lib/analytics";

export default function Resources() {
  useEffect(() => { trackPageView("resources"); }, []);
  const resources = [
    {
      category: "Official Sources",
      items: [
        {
          title: "Houses of the Oireachtas",
          description: "Official website of the Irish Parliament",
          url: "https://www.oireachtas.ie",
          icon: Building2Icon,
        },
        {
          title: "Department of Foreign Affairs",
          description: "Ireland's foreign policy and neutrality position",
          url: "https://www.dfa.ie",
          icon: Globe,
        },
        {
          title: "Defence Forces Ireland",
          description: "Official Irish Defence Forces website",
          url: "https://www.military.ie",
          icon: ShieldIcon,
        },
      ],
    },
    {
      category: "Research & Analysis",
      items: [
        {
          title: "Irish Neutrality Studies",
          description: "Academic research on Irish neutrality",
          url: "#",
          icon: Book,
        },
        {
          title: "Peace & Neutrality Alliance",
          description: "PANA - Campaigning for Irish neutrality",
          url: "https://www.pana.ie",
          icon: Users,
        },
        {
          title: "Afri - Action from Ireland",
          description: "Peace, human rights and justice organization",
          url: "https://www.afri.ie",
          icon: Globe,
        },
      ],
    },
    {
      category: "Educational Materials",
      items: [
        {
          title: "History of Irish Neutrality",
          description: "Comprehensive overview of Ireland's neutral stance",
          url: "#",
          icon: FileText,
        },
        {
          title: "UN Peacekeeping Missions",
          description: "Ireland's contribution to global peace",
          url: "https://peacekeeping.un.org",
          icon: Globe,
        },
        {
          title: "Triple Lock Explained",
          description: "Understanding Ireland's deployment safeguards",
          url: "#",
          icon: Book,
        },
      ],
    },
  ];

  const shareText =
    "I support Ireland's neutrality. Contact your representatives and let them know you do too! #IrishNeutrality";
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl
    )}`,
  };

  return (
    <main data-testid="resources-page" className="pt-24 bg-slate-950 min-h-screen">
      {/* Header */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
              Learn More
            </span>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-50 mt-4 mb-6">
              Resources & <span className="text-emerald-400">Information</span>
            </h1>
            <p className="text-lg text-slate-300">
              Explore these resources to learn more about Irish neutrality, its
              history, and how you can help protect it.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {resources.map((category) => (
              <div key={category.category} data-testid={`resource-category-${category.category.toLowerCase().replace(/\s+/g, '-')}`}>
                <h2 className="font-serif text-2xl font-semibold text-slate-100 mb-6">
                  {category.category}
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {category.items.map((item) => (
                    <a
                      key={item.title}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group card-dark p-6 card-lift"
                      data-testid={`resource-link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600/30 transition-colors">
                          <item.icon className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-100 group-hover:text-emerald-400 transition-colors flex items-center gap-2">
                            {item.title}
                            <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </h3>
                          <p className="text-sm text-slate-400 mt-1">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-16 bg-slate-900" data-testid="share-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Share2 className="w-6 h-6 text-orange-500" />
              <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
                Spread the Word
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mb-6">
              Share This Campaign
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Help us reach more people by sharing this campaign on social media.
              Every voice matters in the fight to protect Irish neutrality.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-twitter-btn"
                className="px-6 py-3 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-full font-medium transition-all hover:-translate-y-0.5"
              >
                Share on X
              </a>
              <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-facebook-btn"
                className="px-6 py-3 bg-[#4267B2] hover:bg-[#365899] text-white rounded-full font-medium transition-all hover:-translate-y-0.5"
              >
                Share on Facebook
              </a>
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin-btn"
                className="px-6 py-3 bg-[#0077B5] hover:bg-[#006097] text-white rounded-full font-medium transition-all hover:-translate-y-0.5"
              >
                Share on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-slate-950" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-slate-100 mb-6">
                Get in Touch
              </h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Have questions about the campaign or want to get more involved?
                We'd love to hear from you. Join the movement to protect Ireland's
                proud tradition of Irish neutrality.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email us at</p>
                    <a
                      href="mailto:admin@ourneutrality.ie"
                      className="text-slate-100 hover:text-emerald-400 transition-colors"
                    >
                      admin@ourneutrality.ie
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Facts */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
              <h3 className="font-serif text-xl font-medium text-emerald-400 mb-6">
                Quick Facts About Irish Neutrality
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1 font-bold">•</span>
                  <span className="text-slate-300">
                    Ireland has been neutral since independence in 1922
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1 font-bold">•</span>
                  <span className="text-slate-300">
                    87 Irish peacekeepers have died serving in UN missions
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1 font-bold">•</span>
                  <span className="text-slate-300">
                    Ireland is one of only 6 EU members not in NATO
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1 font-bold">•</span>
                  <span className="text-slate-300">
                    Over 70% of Irish citizens support maintaining neutrality
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1 font-bold">•</span>
                  <span className="text-slate-300">
                    The Triple Lock requires UN, Government, and Dáil approval for
                    deployments
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-orange-900/10" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            Take action now and contact your representatives about protecting
            Ireland's neutrality.
          </p>
          <Link to="/take-action">
            <Button
              data-testid="resources-cta-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 rounded-full text-lg font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)]"
            >
              Email Your Representatives
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}

// Custom icons
function Building2Icon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  );
}

function ShieldIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
    </svg>
  );
}
